import React, { useState, useRef, useEffect, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import InputFormItem from "src/view/shared/form/items/InputFormItem";
import CheckboxFormItem from "src/view/shared/form/items/CheckboxFormItem";
import Spinner from "src/view/shared/Spinner";
import CheckoutFormV2 from "./CheckoutFormV2";
import MockCheckoutForm from "./MockCheckoutForm";
import { CheckoutFormV2Ref } from "src/types/CheckoutFormV2";
import EntitlementService from "src/modules/entitlement/entitlementService";
import service from "src/modules/auth/authService";
import Errors from "src/modules/shared/error/errors";
import { i18n } from "src/i18n";
import { CheckoutFormV2Props } from "src/types/CheckoutFormV2";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import config from "src/config";
import styles from "./PaymentSection.module.scss";

interface PaymentSectionProps {
  userData: any;
  planList: any[];
  activeTab: string;
  selectedPlan: any;
  setSelectedPlan: (plan: any) => void;
  step1Complete: boolean;
  total: number;
  setTotal: (total: number) => void;
  onPaymentComplete: (success: boolean) => void;
  setActiveTab: (tab: string) => void;
}

const PaymentSection = ({
  userData,
  planList,
  activeTab,
  selectedPlan,
  setSelectedPlan,
  total,
  setTotal,
  onPaymentComplete,
  setActiveTab,
  step1Complete,
}: PaymentSectionProps) => {
  const dispatch = useDispatch();
  const [state, setState] = useState("idle");
  const [couponCode, setCouponCode] = useState("");
  const [couponCodeState, setCouponCodeState] = useState("idle");
  const [couponCodeAppied, setCouponCodeAplied] = useState<string | null>(null);
  const pkRef = useRef<any>(null);
  const [clientSecret, setClientSecret] = useState<any>(null);
  const [subscriptionId, setSubscriptionId] = useState<any>(null);
  const stripeFormRef = useRef<any>(null);
  const stripeFormMounted = useRef(false);
  const [pkLoaded, setPkLoaded] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [cardholderNameTouched, setCardholderNameTouched] = useState(false);
  const [cardholderNameError, setCardholderNameError] = useState<string | null>(
    null
  );
  const [termsOfServiceError, setTermsOfServiceError] =
    useState<boolean>(false);
  const [expandedPlanIds, setExpandedPlanIds] = useState<string[]>([]);
  const [counponId, setCounponId] = useState<string | null>(null);
  const form = useForm({
    mode: "onSubmit",
    defaultValues: {
      promoCode: "",
      cardholderName: "",
      termsOfService: false,
    },
  });

  // Load Stripe on component mount
  useEffect(() => {
    if (step1Complete) {
      fetchStripeKey();
    }
  }, [step1Complete]);

  useEffect(() => {
    // Initialize with all plan IDs
    if (planList?.length > 0) {
      setExpandedPlanIds(planList.map((plan) => plan.id));
    }
  }, [planList]); // Only run when planList changes

  const switchTabs = (tabName) => {
    if (tabName === "Monthly") {
      setActiveTab("Monthly");
    } else if (tabName === "Annually") {
      setActiveTab("Annually");
    }
  };

  // const handleClick = (plan, index) => {
  //   console.log("plan", plan);
  //   // Only reset if selecting a different plan
  //   if (!selectedPlan || selectedPlan.id !== plan.id) {
  //     setSelectedPlan(plan);
  //     setTotal(plan.retailPrice);
  //     setCouponCode("");
  //     setCouponCodeState("idle");
  //     setCouponCodeAplied(null);
  //   }
  // };

  function onCouponCodeChange(event) {
    let value;

    // Handle both direct string values and event objects
    if (typeof event === "string") {
      value = event;
    } else if (event && typeof event === "object") {
      // For React events with target
      if (event.target && event.target.value !== undefined) {
        value = event.target.value;
      } else {
        // For direct value objects or custom events
        value = event.value !== undefined ? event.value : event;
      }
    } else {
      return; // Invalid input
    }

    const _couponCode = (value || "").toUpperCase();
    setCouponCode(_couponCode);
    setCouponCodeState("idle");
    setCouponCodeAplied(null);
  }

  async function validateCouponCode() {
    if (!couponCode) {
      return;
    }

    try {
      setCouponCodeState("loading");
      if (selectedPlan && selectedPlan.storeID) {
        const data = {
          storeID: selectedPlan.storeID,
          couponCode: couponCode,
        };

        const couponCheckResponse = await service.checkCoupon(data);
        if (couponCheckResponse?.status) {
          let amountOff = couponCheckResponse?.couponData?.amount_off;
          let percentOff = couponCheckResponse?.couponData?.percent_off;
          setCounponId(couponCheckResponse?.couponData?.id);
          if (amountOff) {
            const price = selectedPlan.retailPrice;
            amountOff = amountOff / 100;
            const discountedPrice = price - amountOff;
            const truncatedDiscountedPrice =
              Math.floor(discountedPrice * 100) / 100;
            setTotal(parseFloat(truncatedDiscountedPrice.toFixed(2)));
            setCouponCodeState("valid");
            setCouponCodeAplied(
              `$${amountOff} off for ${
                couponCheckResponse?.couponData?.duration
              }. $${truncatedDiscountedPrice.toFixed(2)} today.`
            );
          } else if (percentOff) {
            const price = selectedPlan.retailPrice;
            const discountedPrice = price * (1 - percentOff / 100);
            const truncatedDiscountedPrice =
              Math.floor(discountedPrice * 100) / 100;
            setTotal(parseFloat(truncatedDiscountedPrice.toFixed(2)));
            setCouponCodeState("valid");
            setCouponCodeAplied(
              `${percentOff}% off for ${
                couponCheckResponse?.couponData?.duration
              }. $${truncatedDiscountedPrice.toFixed(2)} today.`
            );
          }
        } else {
          setCouponCode("");
          setCouponCodeState("invalidCoupon");
          setCouponCodeAplied(null);
        }
      } else {
        setCouponCodeState("invalidCouponForEntitlement");
        setCouponCodeAplied(null);
      }
    } catch (error: any) {
      console.log("error??123>>>>>", error.response);
      setCouponCode("");
      setCouponCodeAplied(null);
      if (error.response?.data === "invalidCoupon") {
        console.log("error??123>>>>>", error.response.data);
        setCouponCodeState("invalidCoupon");
      } else if (error.response?.data === "invalidCouponForEntitlement") {
        setCouponCodeState("invalidCouponForEntitlement");
      } else {
        console.log("error??>>>>>", error);
        setCouponCodeState("error");
      }
    }
  }

  // Fetch Stripe key and setup intent
  const fetchStripeKey = async () => {
    try {
      const response = await EntitlementService.setUpIntent();
      const clientSecretData = await EntitlementService.getClientSecret();
      const stripePromise = await loadStripe(
        clientSecretData?.data?.CLIENT_SECRET
      );
      pkRef.current = stripePromise;
      setPkLoaded(true);

      if (response) {
        setClientSecret({
          clientSecret: response?.data?.client_secret,
          paymentMethodOrder: ["card"],
          card: {
            style: {
              base: {
                fontSize: "16px",
                color: "#ffffff",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#E88697",
              },
            },
          },
        });
      }

      return stripePromise;
    } catch (error) {
      console.error("Error fetching Stripe key:", error);
      return null;
    }
  };

  const handleCardholderNameChange = (value) => {
    setCardholderName(value);
    setCardholderNameTouched(true);
    if (!value.trim()) {
      setCardholderNameError("Cardholder name is required");
    } else {
      setCardholderNameError(null);
    }
  };

  const onSubmit = async (data, event) => {
    let flagValidation = false;
    if (event) event.preventDefault();

    if (!selectedPlan) {
      alert("Please select a plan before proceeding");
      return;
    }

    // Get the cardholder name from form data instead of state
    const formCardholderName = data.cardholderName;
    setCardholderName(formCardholderName);

    // Validate cardholder name
    if (!formCardholderName.trim()) {
      setCardholderNameTouched(true);
      setCardholderNameError("Cardholder name is required");
      flagValidation = true;
    }

    // Validate terms of service
    if (!data.termsOfService) {
      setTermsOfServiceError(true);
      flagValidation = true;
    } else {
      setTermsOfServiceError(false);
    }

    // return;
    if (flagValidation) {
      return;
    }
    // Let CheckoutFormV2 handle payment, but only set loading state if validation passes
    if (
      stripeFormRef.current &&
      typeof stripeFormRef.current.submitPayment === "function"
    ) {
      try {
        // submitPayment now returns validation result
        const isValid = stripeFormRef.current.submitPayment();

        // Only set loading state if validation passed
        if (isValid === true && !flagValidation) {
          setState("loading");
        }
      } catch (e) {
        console.error("Error calling submitPayment", e);
        setState("idle");
      }
    } else {
      console.error("Stripe form not ready");
      setState("idle");
    }
  };

  // Handle the payment completion event coming from CheckoutFormV2
  const handlePaymentComplete = (success) => {
    setState("idle");
    if (success) {
      // Notify parent component
      if (onPaymentComplete) {
        onPaymentComplete(true);
      }

      // Redirect to survey page
      window.location.href = "/auth/survey";
    }
  };

  const warningIcon = `/images/warning-icon.svg`;
  const userUrl = `/images/user.svg`;
  const circleDownIcon = `/images/circle-down.svg`;
  const circleDown2Icon = `/images/circle-down2.svg`;

  // Format total for display
  const formattedTotal = typeof total === "number" ? total.toFixed(2) : "0.00";

  const togglePlanDescription = (planId: string) => {
    setExpandedPlanIds((prevIds) => {
      if (prevIds.includes(planId)) {
        return prevIds.filter((id) => id !== planId);
      } else {
        return [...prevIds, planId];
      }
    });
  };

  return (
    <>
      {state === "loading" && (
        <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-screen bg-gray-500/60">
          <Spinner />
        </div>
      )}

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={styles.container}
        >
          <div className="my-[10px]">
            <h2 className={styles.sectionTitle}>2. Details</h2>
          </div>

          <div className={`!box-shadow-none !bg-transparent !px-0`}>
            <div className="w-full">
              {planList
                .filter((plan) => plan.title === "Full Access")
                .map((plan, index) => (
                  <div key={plan.id} className="w-full">
                    <div
                      className={`${styles.planCard} ${
                        expandedPlanIds.includes(plan.id) ? "min-h-[350px]" : ""
                      } active
                      relative flex h-auto w-full flex-col justify-between rounded-[10px] bg-[#FFFFFF15]`}
                    >
                      <div>
                        <h2
                          className={`${styles.planTitle} font-syneBold text-[#fff]`}
                        >
                          {plan.title}
                        </h2>

                        {/* Price display section */}
                        {couponCodeState === "valid" &&
                        selectedPlan &&
                        selectedPlan.id === plan.id ? (
                          <>
                            <div className={styles.priceContainer}>
                              <div className="relative">
                                <span className={`${styles.price} text-[#fff]`}>
                                  <span className={styles.currency}>$</span>
                                  29.99
                                </span>
                                <span
                                  className={`${styles.period} text-[#fff]`}
                                >
                                  /mo.
                                </span>
                                <div className="absolute left-0 top-1/2 z-10 h-0 w-full -translate-y-1/2 transform border-t-[1.5px] border-[#003A4B]"></div>
                              </div>
                            </div>
                            <div className={styles.billingInfo}>
                              <span className="text-[#fff]">
                                Billed as $29.99/mo.
                              </span>
                              <div className="absolute left-0 top-1/2 z-10 h-0 w-full -translate-y-1/2 transform border-t-[1.5px] border-[#003A4B]"></div>
                            </div>
                            <div className="mt-3">
                              <p className={`${styles.features} text-[#fff]`}>
                                After Discount
                              </p>
                            </div>

                            <div className={styles.priceContainer}>
                              <span className={`${styles.price} text-[#fff]`}>
                                <span className={styles.currency}>$</span>
                                {formattedTotal}
                              </span>
                              <span
                                className={`${styles.period} text-[#005363]`}
                              >
                                /mo.
                              </span>
                            </div>
                            <div className={styles.billingInfo}>
                              <span className="text-[#fff]">
                                Billed as ${formattedTotal}/mo.
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={styles.priceContainer}>
                              <span className={`${styles.price} text-[#fff]`}>
                                <span className={styles.currency}>$</span>
                                29.99
                              </span>
                              <span
                                className={`${styles.period} italic text-[#fff]`}
                              >
                                /mo.
                              </span>
                            </div>
                            <div className={styles.billingInfo}>
                              <span className="font-plusJakartaSans italic text-[#7CA7AD]">
                                Billed after promotional period
                              </span>
                            </div>
                          </>
                        )}

                        <div className={styles.divider}></div>

                        <div className={`${styles.features} text-[#fff]`}>
                          <div
                            className="mb-[15px] flex cursor-pointer items-center justify-between text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlanDescription(plan.id);
                            }}
                          >
                            <p className="mr-[10px] font-plusJakartaSans text-[14px] font-[700]">
                              Includes:
                            </p>
                            <img
                              src={circleDown2Icon}
                              alt="circle-down"
                              className={`inline-block h-[14px] w-[14px] transition-transform duration-200 ${
                                expandedPlanIds.includes(plan.id)
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </div>
                          <div
                            className={`overflow-hidden transition-all duration-200 ${
                              expandedPlanIds.includes(plan.id)
                                ? "max-h-[500px] opacity-100"
                                : "max-h-0 opacity-0"
                            }`}
                          >
                            {plan?.description
                              ?.split("\n")
                              ?.map((bulletPoint, i, array) => (
                                <p
                                  key={i}
                                  className={`${
                                    i === array.length - 1 ? "" : "mb-[15px]"
                                  } text-[#FFFFFF]`}
                                >
                                  {bulletPoint.replace("•", "✓")}
                                </p>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="my-[10px]">
            <h2 className={styles.sectionTitle}>3. Redeem Promo Code</h2>
          </div>

          <div className={`${styles.card} !py-[20px]`}>
            <div>
              <div className="flex items-center justify-between gap-2 sm:gap-5">
                <div className="flex-1">
                  <InputFormItem
                    name="promoCode"
                    placeholder="Promo code"
                    onChange={(value) => {
                      onCouponCodeChange(value);
                      form.setValue(
                        "promoCode",
                        typeof value === "string"
                          ? value
                          : value?.target?.value || ""
                      );
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={validateCouponCode}
                  className={`${styles.button} w-[150px] bg-white text-[#035C67]`}
                >
                  Redeem
                </button>
              </div>

              {/* Promo code messages */}
              {couponCodeAppied && (
                <p className="mt-[20px] text-[12px] text-white sm:text-[14px]">{`Coupon applied! ${couponCodeAppied}`}</p>
              )}
              {!couponCodeAppied && couponCodeState === "invalidCoupon" && (
                <div className="mt-[20px] inline-flex w-[162px] items-center gap-2 rounded-[12px] bg-[#E88697] p-[12px] sm:mt-[30px] sm:p-[15px]">
                  <img
                    src={warningIcon}
                    alt="warning"
                    className="h-[18px] w-[18px] sm:h-[22px] sm:w-[22px]"
                  />
                  <p className="text-[12px] text-white sm:text-[14px]">
                    Invalid Coupon
                  </p>
                </div>
              )}
              {!couponCodeAppied &&
                couponCodeState === "invalidCouponForEntitlement" && (
                  <div className="mt-[30px] inline-flex items-center gap-2 rounded-lg bg-[#E88697] p-[15px]">
                    <img
                      src={warningIcon}
                      alt="warning"
                      className="h-[22px] w-[22px]"
                    />
                    <p className="text-xs text-white md:text-sm">
                      This promo code is not valid for the selected plan.
                    </p>
                  </div>
                )}
              {!couponCodeAppied && couponCodeState === "error" && (
                <div className="mt-[30px] inline-flex items-center gap-2 rounded-lg bg-[#E88697] p-[15px]">
                  <img
                    src={warningIcon}
                    alt="warning"
                    className="h-[22px] w-[22px]"
                  />
                  <p className="text-xs text-white md:text-sm">
                    An error occurred. Please try again.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details Section */}
          <div className="">
            <h2 className={styles.sectionTitle}>4. Account Information</h2>
            <span
              className={`${styles.sectionSubtitle} font-plusJakartaSans font-[400]`}
            >
              Free promotional period, then <br />
              $29.99/month until canceled
            </span>
          </div>

          <div className={styles.paymentSection}>
            <div className={`${styles.card} !py-[20px]`}>
              <div className={styles.cardHeader}>
                <span className="inline-block">
                  <svg
                    width="19"
                    height="14"
                    viewBox="0 0 19 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      opacity="0.6"
                      d="M1.539 0H17.461C17.8692 0 18.2606 0.156244 18.5492 0.43436C18.8379 0.712476 19 1.08968 19 1.483V12.517C19 12.7118 18.9602 12.9046 18.8829 13.0845C18.8055 13.2644 18.6921 13.4279 18.5492 13.5656C18.4063 13.7033 18.2367 13.8126 18.0499 13.8871C17.8632 13.9616 17.6631 14 17.461 14H1.539C1.33689 14 1.13677 13.9616 0.95005 13.8871C0.76333 13.8126 0.593672 13.7033 0.450762 13.5656C0.307854 13.4279 0.19449 13.2644 0.117149 13.0845C0.0398064 12.9046 0 12.7118 0 12.517V1.483C0 1.28825 0.0398064 1.09541 0.117149 0.91548C0.19449 0.735554 0.307854 0.572069 0.450762 0.43436C0.593672 0.296651 0.76333 0.187414 0.95005 0.112887C1.13677 0.0383589 1.33689 0 1.539 0ZM17.461 0.917466H1.539C1.38352 0.918002 1.23456 0.977757 1.12462 1.0837C1.01468 1.18964 0.952667 1.33317 0.952112 1.483V2.99448H18.0521V1.483C18.0518 1.40846 18.0363 1.33471 18.0065 1.26595C17.9766 1.1972 17.933 1.13478 17.8781 1.08226C17.8232 1.02975 17.7581 0.988166 17.6866 0.95989C17.615 0.931614 17.5383 0.917198 17.461 0.917466ZM1.539 13.0825H17.461C17.6165 13.082 17.7654 13.0222 17.8754 12.9163C17.9853 12.8104 18.0473 12.6668 18.0479 12.517V5.77942H0.947887V12.517C0.948166 12.5915 0.963675 12.6653 0.99353 12.734C1.02339 12.8028 1.06701 12.8652 1.12189 12.9177C1.17678 12.9703 1.24187 13.0118 1.31344 13.0401C1.38501 13.0684 1.46165 13.0828 1.539 13.0825ZM9.28889 8.04359H16.2809C16.3929 8.04359 16.5003 8.00072 16.5794 7.92442C16.6586 7.84812 16.7031 7.74464 16.7031 7.63673C16.7031 7.52883 16.6586 7.42534 16.5794 7.34904C16.5003 7.27274 16.3929 7.22987 16.2809 7.22987H9.28889C9.17691 7.22987 9.06951 7.27274 8.99033 7.34904C8.91115 7.42534 8.86667 7.52883 8.86667 7.63673C8.86667 7.74464 8.91115 7.84812 8.99033 7.92442C9.06951 8.00072 9.17691 8.04359 9.28889 8.04359ZM12.7849 9.87445H16.2809C16.3929 9.87445 16.5003 9.83159 16.5794 9.75529C16.6586 9.67899 16.7031 9.5755 16.7031 9.4676C16.7031 9.35969 16.6586 9.2562 16.5794 9.1799C16.5003 9.1036 16.3929 9.06074 16.2809 9.06074H12.7849C12.6729 9.06074 12.5655 9.1036 12.4863 9.1799C12.4072 9.2562 12.3627 9.35969 12.3627 9.4676C12.3627 9.5755 12.4072 9.67899 12.4863 9.75529C12.5655 9.83159 12.6729 9.87445 12.7849 9.87445Z"
                      fill="white"
                    />
                  </svg>
                </span>
                <span className="text-[11px] leading-[16px] opacity-60 sm:text-[13px]">
                  Card Information
                </span>
              </div>

              {/* Stripe Elements */}
              <div className={styles.stripeContainer}>
                {pkRef.current && clientSecret && step1Complete ? (
                  <Elements stripe={pkRef.current} options={clientSecret}>
                    <CheckoutFormV2
                      ref={stripeFormRef}
                      clientSecret={clientSecret}
                      priceID={selectedPlan?.storeID}
                      state={state}
                      setState={setState}
                      subscriptionId={subscriptionId}
                      couponCode={couponCode}
                      couponCodeAppied={couponCodeAppied}
                      couponId={counponId}
                      amount={total}
                      selectedPlan={selectedPlan}
                      onPaymentComplete={handlePaymentComplete}
                      cardholderName={form.watch("cardholderName") || ""}
                      customStyles={{
                        base: {
                          fontSize: "14px",
                          color: "#ffffff",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          "::placeholder": {
                            color: "#ffffff",
                            opacity: "0.6",
                          },
                          padding: "12px",
                          "@media (min-width: 640px)": {
                            fontSize: "16px",
                            padding: "16px",
                          },
                        },
                        invalid: {
                          color: "#E88697",
                        },
                      }}
                    />
                  </Elements>
                ) : !step1Complete ? (
                  <MockCheckoutForm />
                ) : (
                  <div className="flex justify-center">
                    <Spinner />
                  </div>
                )}
              </div>
            </div>

            {/* Cardholder Information */}
            <div className={`${styles.card} !py-[20px]`}>
              <div className={styles.cardHeader}>
                <span className="inline-block">
                  <svg
                    width="12"
                    height="16"
                    viewBox="0 0 12 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g opacity="0.6">
                      <path
                        d="M5.67928 8.44531C2.38874 8.44531 0 11.248 0 15.1096C0 15.6006 0.397456 15.998 0.888479 15.998H10.4693C10.9603 15.998 11.3578 15.6006 11.3578 15.1096C11.3578 11.248 8.96956 8.44531 5.67928 8.44531Z"
                        fill="white"
                      />
                      <path
                        d="M5.67961 7.10863C7.63997 7.10863 9.23379 5.51454 9.23379 3.55445C9.23379 1.59436 7.6397 0 5.67961 0C3.71952 0 2.12543 1.59409 2.12543 3.55418C2.12543 5.51427 3.71925 7.10863 5.67961 7.10863Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                </span>
                <span className="text-[11px] leading-[16px] opacity-60 sm:text-[13px]">
                  Enter your full name on the card.
                </span>
              </div>

              <div>
                {step1Complete ? (
                  <InputFormItem
                    name="cardholderName"
                    placeholder="Enter your full name on the card"
                    hasDarkBackground={true}
                    onChange={handleCardholderNameChange}
                    noMessageError={true}
                    externalErrorMessage={
                      cardholderNameTouched && cardholderNameError
                        ? cardholderNameError
                        : null
                    }
                  />
                ) : (
                  <div className="flex h-[46px] items-center justify-between rounded-md bg-white/30 px-5 leading-[46px] text-white opacity-60 sm:h-[50px] sm:leading-[50px]">
                    <span className="text-[12px] sm:text-[14px]">
                      Enter full name on card
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4">
                {step1Complete ? (
                  <CheckboxFormItem
                    name="termsOfService"
                    label={
                      <span
                        className={`text-[12px] !font-[300] sm:text-[14px] ${
                          termsOfServiceError ? "!text-[#E88697]" : "text-white"
                        }`}
                      >
                        I agree with{" "}
                        <a
                          href={config.marketingPrivacyPolicyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline ${
                            termsOfServiceError
                              ? "!text-[#E88697]"
                              : "text-white"
                          }`}
                        >
                          Privacy Policy
                        </a>{" "}
                        and{" "}
                        <a
                          className={`text-white underline ${
                            termsOfServiceError
                              ? "!text-[#E88697]"
                              : "text-white"
                          }`}
                          href={config.marketingTermsOrServiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms Of Service
                        </a>
                        .
                      </span>
                    }
                    ignoredErrorTypes={["required", "oneOf"]}
                  />
                ) : (
                  <div className="flex h-[24px] items-center rounded-md bg-white/30 px-5 text-white opacity-60">
                    <span className="text-[12px] sm:text-[14px]">
                      I agree with Privacy Policy and Terms of Service
                    </span>
                  </div>
                )}
                {termsOfServiceError && (
                  <div className="ml-1 mt-1 text-[12px] text-[#E88697] sm:text-[14px]">
                    {termsOfServiceError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              disabled={
                state === "loading" ||
                couponCodeState === "loading" ||
                !selectedPlan ||
                !step1Complete ||
                (couponCode && couponCodeState !== "valid")
              }
              type="submit"
              className={`${styles.button} w-[150px] bg-white text-[#035C67] disabled:text-[#035C67] disabled:opacity-30`}
            >
              Subscribe
            </button>
            {/* Error message when coupon code is entered but not validated */}
            {couponCode &&
              couponCodeState !== "valid" &&
              couponCodeState !== "loading" && (
                <div className="mt-3 text-center">
                  <p className="text-[12px] text-[#E88697] sm:text-[14px]">
                    {couponCodeState === "invalidCoupon"
                      ? "Please enter a valid coupon code or remove it to continue"
                      : couponCodeState === "invalidCouponForEntitlement"
                      ? "This coupon is not valid for the selected plan. Please remove it or use a different coupon"
                      : couponCodeState === "error"
                      ? "There was an error validating the coupon. Please try again or remove it to continue"
                      : "Please validate your coupon code by clicking 'Redeem' or remove it to continue"}
                  </p>
                </div>
              )}
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default PaymentSection;
